/**
 * Browser diagnostics ring buffers.
 *
 * Captures the most recent console messages and network requests on the page,
 * so a user filing a support ticket can attach the context that led up to the
 * problem without having to reproduce it.
 *
 * Privacy notes:
 *   - Console messages are stringified with a 1KB cap per entry.
 *   - Network entries record method/url/status/duration only — never bodies.
 *     Query strings are kept because they often carry the failing id.
 *   - The user opts in by clicking "Submit ticket"; capture is local until then.
 */

import type {
  ConsoleLogEntry,
  ConsoleLogLevel,
  NetworkLogEntry,
  SupportTicketBrowserInfo,
} from "@repo/shared/types";

const CONSOLE_LIMIT = 100;
const NETWORK_LIMIT = 50;
const ARG_CAP = 1024;

const consoleBuffer: ConsoleLogEntry[] = [];
const networkBuffer: NetworkLogEntry[] = [];

let installed = false;

function safeStringify(arg: unknown): string {
  if (arg instanceof Error) return `${arg.name}: ${arg.message}\n${arg.stack ?? ""}`;
  if (typeof arg === "string") return arg;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

function pushConsole(level: ConsoleLogLevel, args: unknown[]) {
  const message = args.map(safeStringify).join(" ").slice(0, ARG_CAP);
  consoleBuffer.push({ level, timestamp: new Date().toISOString(), message });
  if (consoleBuffer.length > CONSOLE_LIMIT) consoleBuffer.shift();
}

function pushNetwork(entry: NetworkLogEntry) {
  networkBuffer.push(entry);
  if (networkBuffer.length > NETWORK_LIMIT) networkBuffer.shift();
}

function installConsoleHooks() {
  const levels: ConsoleLogLevel[] = ["log", "info", "warn", "error", "debug"];
  for (const level of levels) {
    const original = (console as unknown as Record<string, (...a: unknown[]) => void>)[level];
    if (typeof original !== "function") continue;
    (console as unknown as Record<string, (...a: unknown[]) => void>)[level] = (
      ...args: unknown[]
    ) => {
      try {
        pushConsole(level, args);
      } catch {
        // never let logging break the app
      }
      original.apply(console, args);
    };
  }
}

function installFetchHook() {
  const originalFetch = window.fetch.bind(window);
  const hooked = async (...args: Parameters<typeof fetch>) => {
    const start = performance.now();
    const [input, init] = args;
    const url = typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const method = (init?.method ?? "GET").toUpperCase();
    try {
      const res = await originalFetch(...args);
      pushNetwork({
        method,
        url,
        status: res.status,
        durationMs: Math.round(performance.now() - start),
        timestamp: new Date().toISOString(),
      });
      return res;
    } catch (err) {
      pushNetwork({
        method,
        url,
        durationMs: Math.round(performance.now() - start),
        timestamp: new Date().toISOString(),
        error: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  };
  // Bun's globals declare `fetch` as a function *and* a namespace (Bun.preconnect),
  // so `typeof window.fetch` carries statics a plain wrapper cannot have.
  window.fetch = hooked as typeof window.fetch;
}

function installXhrHook() {
  const OrigXhr = window.XMLHttpRequest;
  if (!OrigXhr) return;
  const open = OrigXhr.prototype.open;
  const send = OrigXhr.prototype.send;

  OrigXhr.prototype.open = function (this: any, method: string, url: string, ...rest: any[]) {
    this.__diagMethod = (method ?? "GET").toUpperCase();
    this.__diagUrl = typeof url === "string" ? url : String(url);
    // @ts-expect-error pass through
    return open.call(this, method, url, ...rest);
  };

  OrigXhr.prototype.send = function (this: any, body?: any) {
    const start = performance.now();
    const method: string = this.__diagMethod ?? "GET";
    const url: string = this.__diagUrl ?? "";
    const onDone = () => {
      pushNetwork({
        method,
        url,
        status: this.status,
        durationMs: Math.round(performance.now() - start),
        timestamp: new Date().toISOString(),
      });
    };
    const onError = () => {
      pushNetwork({
        method,
        url,
        durationMs: Math.round(performance.now() - start),
        timestamp: new Date().toISOString(),
        error: "xhr error",
      });
    };
    this.addEventListener("loadend", onDone, { once: true });
    this.addEventListener("error", onError, { once: true });
    return send.call(this, body);
  };
}

export function installBrowserDiagnostics() {
  if (installed || typeof window === "undefined") return;
  installed = true;
  try {
    installConsoleHooks();
    installFetchHook();
    installXhrHook();
  } catch {
    // best-effort; never break page load
  }
}

export function getBrowserDiagnostics(): SupportTicketBrowserInfo {
  return {
    userAgent: navigator.userAgent,
    url: window.location.href,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    language: navigator.language,
    capturedAt: new Date().toISOString(),
    console: [...consoleBuffer],
    network: [...networkBuffer],
  };
}
